import React from 'react';
import styled from 'styled-components';
import { fontSans, inset, ink, space, typeScale, weight } from '@repo/design-tokens';
import { Button, Card, DropZone, Icon } from '@repo/design-system';
import type { DropZoneRejectReason, DropZoneTarget } from '@repo/design-system';
import { IconDisc } from '@repo/ui';
import type { IconDiscTone } from '@repo/ui';
import type { IconName } from '@repo/design-system';

/**
 * **The Equipment step on an empty register** (`equipment-page-start`, R3–R6, R8), from frame 01
 * of Rob's 12 October *Equipment Page* design: the design system's `DropZone` under the twin it
 * uploads to, four tiles saying what the file can hold, and the ways out (the adoption file, Skip
 * to Floor Plan, Add an Asset).
 *
 * A page composition, built from design-system parts — `DropZone`, `Button`, `Icon` and `IconDisc` —
 * as Floor Plan's `StartChooser` is, and promoted only if a second host needs it. It holds no upload
 * state: `AssetCsvUploader` owns the file, the check, the mode and the upload, and renders this with
 * its own check and upload controls in `below`.
 *
 * ## Values
 *
 * Read off frame 01 and its design file (`Equipment Page.dc.html`, the start screen): a column
 * centred both ways with 22 px between its parts (`space[5]` — the nearest step; `inset[6]` is a
 * control's inside), tiles 180 px wide 12 px apart — each the design system's `Card` (on-glass, sm),
 * whose 10 px radius stands in for the frame's 12 — titles 13/18 semibold and sentences 12/16 in
 * `ink[2]`, the links 12/16 in `ink[3]` 24 px
 * apart, the link texts in the text `Button`'s default ink. Frame 01 draws them in `--tl-cyan-press`,
 * which at 12 px is 4.03:1 on white, under WCAG 1.4.3's 4.5:1, so the compliant default is used and the
 * drawn colour waits on a darker brand ink from the design system (PROVENANCE.md, for Rob).
 * 13 px has no type step, so the title's font is a constant — the `DropZone` exemption.
 * Two of the parts are not in the frame and await Rob's ratification (`PROVENANCE.md`, *The
 * Equipment frame*): the *Add an Asset* link, and the adoption mode's words.
 */

export interface EquipmentStartProps {
  /** Adoption mode (R8): the title and the first link change; nothing else does. */
  adopting: boolean;
  target: DropZoneTarget;
  onFile: (file: File) => void;
  onReject: (files: File[], reason: DropZoneRejectReason) => void;
  /** *Download Template* (R10): its hook's state, shown as `loading`, never `disabled`. */
  download: { onClick: () => void; downloading: boolean };
  /** *Use the adoption file* / *Use the customer file*. */
  onToggleMode: () => void;
  /** *Skip, and build it on Floor Plan from the Library* — the step's Skip (R6). */
  onSkip: () => void;
  /** *Add an Asset* — the add form (R6). */
  onAddAsset: () => void;
  /** The host is undoing an import: the mode cannot change meanwhile. */
  disabled?: boolean;
  /** The zone's root, so the host can return focus to *Choose File*. */
  dropZoneRef?: React.Ref<HTMLDivElement>;
  /** The host's own lines under the zone: the chosen file, its check, the upload. */
  below?: React.ReactNode;
}

/** R5's four tiles, in the order the data matters. */
const TILES: readonly {
  title: string;
  text: string;
  icon: IconName;
  variant: 'solid' | 'regular';
  tone: IconDiscTone;
}[] = [
  {
    title: 'Name',
    text: 'The only column we need. Repeats are numbered for you.',
    icon: 'tag',
    variant: 'solid',
    tone: 'aqua',
  },
  {
    title: 'Asset Identifier',
    text: 'Your asset system’s ID. Lets you upload an updated file later.',
    icon: 'barcode',
    variant: 'solid',
    tone: 'done',
  },
  {
    title: 'What it is',
    text: 'Manufacturer, product line, model, SKU, category. Any of them helps us identify it.',
    icon: 'industry',
    variant: 'solid',
    tone: 'identify',
  },
  {
    title: 'Costs and dates',
    text: 'Optional. Kept if you have them, never asked for.',
    icon: 'calendar',
    variant: 'regular',
    tone: 'neutral',
  },
];

// Frame 01's tile type with no step (13 px) — the Note / EmptyPane / DropZone exemption.
const TILE_TITLE_FONT = `${weight.semibold} 13px / 18px ${fontSans}`;
const TILE_WIDTH = '180px';

const EquipmentStart: React.FC<EquipmentStartProps> = ({
  adopting,
  target,
  onFile,
  onReject,
  download,
  onToggleMode,
  onSkip,
  onAddAsset,
  disabled = false,
  dropZoneRef,
  below,
}) => (
  <Root data-testid="equipment-start">
    <DropZone
      ref={dropZoneRef}
      title={adopting ? 'Drop the adoption file here' : 'Drop the asset register here'}
      icon={<Icon name="file-csv" variant="solid" size="2xl" />}
      accept=".csv,text/csv"
      onFile={onFile}
      onReject={onReject}
      chooseIcon={<Icon name="folder-open" variant="solid" size="sm" />}
      actions={
        <Button
          type="button"
          iconLeft={<Icon name="download" variant="solid" size="sm" />}
          loading={download.downloading}
          onClick={download.onClick}
        >
          Download Template
        </Button>
      }
      target={target}
    >
      A CSV straight out of your asset management system is fine. We check it with you first;
      nothing is saved until you choose Upload.
    </DropZone>

    {below}

    <Tiles aria-label="What the file can hold">
      {TILES.map(tile => (
        <li key={tile.title}>
          {/* The design system's card on the main panel's glass, as Floor Plan's StartChooser
              does — not a private copy of its look (review). */}
          <Card surface="on-glass" size="sm" className="tile">
            <div className="tile-head">
              <IconDisc tone={tile.tone} size={26}>
                <Icon name={tile.icon} variant={tile.variant} size="xs" />
              </IconDisc>
              <span className="tile-title">{tile.title}</span>
            </div>
            <span className="tile-text">{tile.text}</span>
          </Card>
        </li>
      ))}
    </Tiles>

    <Links>
      <span>
        <Icon name="arrows-rotate" variant="solid" size="xs" />
        {adopting
          ? 'Uploading the customer’s own register?'
          : 'Adopting a twin that is already live?'}
        <Button type="button" variant="text" size="xs" disabled={disabled} onClick={onToggleMode}>
          {adopting ? 'Use the customer file' : 'Use the adoption file'}
        </Button>
      </span>
      <span>
        <Icon name="forward" variant="solid" size="xs" />
        No register?
        <Button type="button" variant="text" size="xs" onClick={onSkip}>
          Skip, and build it on Floor Plan from the Library
        </Button>
      </span>
      <span>
        <Icon name="plus" variant="solid" size="xs" />
        Only a few machines?
        {/* `data-register-add`: where focus lands after *Remove all* empties the register — the
            empty table's *Add an asset* carried it before this screen replaced the table
            (`removeAllReturnFocus`). */}
        <Button type="button" variant="text" size="xs" onClick={onAddAsset} data-register-add="">
          Add an Asset
        </Button>
      </span>
    </Links>
  </Root>
);

export default EquipmentStart;

/*
 * Plain `styled` elements only, with the design-system parts rendered INSIDE them, never
 * `styled(DropZone)` at module scope: the wizard's page suites mock `@repo/design-system` with just
 * the parts each one renders (the rule `EquipmentStage.tsx` states).
 */
const Root = styled.div`
  flex: 1;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${space[5]};
  box-sizing: border-box;
  padding: ${space[4]} 0;

  > .tl-drop-zone {
    width: 100%;
  }
`;

const Tiles = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, ${TILE_WIDTH});
  gap: ${space[3]};
  justify-content: center;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, ${TILE_WIDTH});
  }

  li {
    display: flex;
  }

  /* The card's own sm geometry (12 px padding, 10 px radius, 8 px gap) stands in for the frame's
     12 px radius and 6 px gap - recorded for Rob in PROVENANCE.md. */
  .tile {
    flex: 1;
  }

  .tile-head {
    display: flex;
    align-items: center;
    gap: ${space[2]};
  }

  .tile-title {
    font: ${TILE_TITLE_FONT};
    color: ${ink[1]};
  }

  .tile-text {
    font-family: ${fontSans};
    font-size: ${typeScale.caption1.fontSize};
    line-height: ${typeScale.caption1.lineHeight};
    color: ${ink[2]};
  }
`;

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  column-gap: ${space[6]};
  row-gap: ${space[2]};
  font-family: ${fontSans};
  font-size: ${typeScale.caption1.fontSize};
  line-height: ${typeScale.caption1.lineHeight};
  color: ${ink[3]};

  > span {
    display: inline-flex;
    align-items: center;
    gap: ${inset[2]};
  }
`;
